import React, { useState, useEffect, useMemo } from 'react';
import { Search, X, ChevronRight, Package, FileText, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { products } from '@/data/products';
import { applicationAreas } from '@/data/site';
import { useNavigate } from '@/lib/router';

interface SearchItem {
  id: string;
  title: string;
  description: string;
  url: string;
  category: 'product' | 'application' | 'page';
  icon: typeof Package;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  // Build searchable items
  const searchItems: SearchItem[] = useMemo(() => {
    const items: SearchItem[] = [];

    // Products
    products.forEach(product => {
      items.push({
        id: product.id,
        title: product.model,
        description: product.name,
        url: `/products/${product.category}/${product.id}`,
        category: 'product',
        icon: Package
      });
    });

    // Applications
    applicationAreas.forEach(app => {
      items.push({
        id: app.id,
        title: app.title,
        description: app.description,
        url: `/applications/${app.id}`,
        category: 'application',
        icon: Zap
      });
    });

    // Static pages
    const pages = [
      { title: 'Home', description: 'KeyGoTech - Advanced Acoustic Imaging Solutions', url: '/' },
      { title: 'Products', description: 'Browse all acoustic imaging products', url: '/products' },
      { title: 'Handheld Acoustic Imagers', description: 'Portable acoustic imaging devices', url: '/products/handheld' },
      { title: 'Fixed Acoustic Imagers', description: 'Stationary monitoring systems', url: '/products/fixed' },
      { title: 'Acoustic Camera Modules', description: 'OEM integration modules', url: '/products/modules' },
      { title: 'Data Acquisition Systems', description: 'Multi-channel DAQ hardware', url: '/products/daq' },
      { title: 'NVH Software', description: 'SignalPad analysis software', url: '/products/nvh-software' },
      { title: 'Traffic Monitoring', description: 'Traffic noise monitoring systems', url: '/products/traffic-monitoring' },
      { title: 'Applications', description: 'Industry solutions and use cases', url: '/applications' },
      { title: 'About Us', description: 'Learn about KeyGoTech', url: '/about' },
      { title: 'Contact', description: 'Get in touch with us', url: '/contact' },
      { title: 'Support', description: 'Technical support and resources', url: '/support' },
      { title: 'Downloads', description: 'Product manuals and software', url: '/support/downloads' },
      { title: 'FAQ', description: 'Frequently asked questions', url: '/support/faq' }
    ];

    pages.forEach((page, index) => {
      items.push({
        id: `page-${index}`,
        title: page.title,
        description: page.description,
        url: page.url,
        category: 'page',
        icon: FileText
      });
    });

    return items;
  }, []);

  // Filter results based on query
  const results = useMemo(() => {
    if (!query.trim()) return [];
    
    const lowerQuery = query.toLowerCase();
    return searchItems.filter(item => 
      item.title.toLowerCase().includes(lowerQuery) ||
      item.description.toLowerCase().includes(lowerQuery) ||
      item.category.toLowerCase().includes(lowerQuery)
    ).slice(0, 10);
  }, [query, searchItems]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (!isOpen) {
          // This would need to be handled by parent
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleResultClick = (url: string) => {
    navigate(url);
    onClose();
    setQuery('');
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'product': return 'Product';
      case 'application': return 'Application';
      case 'page': return 'Page';
      default: return category;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'product': return 'bg-blue-100 text-blue-700';
      case 'application': return 'bg-green-100 text-green-700';
      case 'page': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Search Header */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-200">
          <Search className="w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search products, applications, pages..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 border-0 focus-visible:ring-0 text-lg placeholder:text-slate-400"
            autoFocus
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Search Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {query.trim() && results.length === 0 && (
            <div className="px-4 py-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 font-medium">No results found</p>
              <p className="text-slate-400 text-sm mt-1">Try different keywords</p>
            </div>
          )}

          {results.length > 0 && (
            <div className="py-2">
              {results.map((result) => {
                const Icon = result.icon;
                return (
                  <button
                    key={result.id}
                    onClick={() => handleResultClick(result.url)}
                    className="w-full px-4 py-3 flex items-center gap-4 hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900 truncate">
                          {result.title}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getCategoryColor(result.category)}`}>
                          {getCategoryLabel(result.category)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 truncate">
                        {result.description}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )}

          {!query.trim() && (
            <div className="px-4 py-6">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
                Quick Links
              </p>
              <div className="space-y-1">
                {searchItems
                  .filter(item => 
                    item.url === '/products/handheld/des-c136' ||
                    item.url === '/applications/partial-discharge' ||
                    item.url === '/contact' ||
                    item.url === '/support/downloads'
                  )
                  .map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleResultClick(item.url)}
                        className="w-full px-3 py-2 flex items-center gap-3 hover:bg-slate-50 rounded-lg transition-colors text-left"
                      >
                        <Icon className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-700">{item.title}</span>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-3">
            <span>Press <kbd className="px-1.5 py-0.5 bg-white rounded border">ESC</kbd> to close</span>
          </div>
          <span>{results.length} results</span>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
